/**
 * AdoraCare - the intake-start door client contract
 * (IDENTITY-LINK-AT-INTAKE-V1).
 *
 * The ONE typed client binding for POST /api/appointments/:id/intake/start
 * now that the start verdict carries the identity door
 * ('selection_required' / 'linked' / 'started'). Lives HERE (not in
 * dashboard/api.ts) so the open tooth build's chartered api.ts edits and
 * this package never intersect (the P2 fence in
 * docs/AGENT-RUNS/IDENTITY-LINK-AT-INTAKE-V1.md); dashboard/api.ts stays
 * byte-identical. Same endpoint, same envelope, same portal client - never
 * a second transport.
 */

import { portalApiPost } from '@/lib/portal-api';
import type { IntakeSessionView } from '@/dashboard/api';

export interface IntakeIdentityCandidateView {
  patientId: string;
  umr: string;
  fullName: string;
  gender: string;
  ageYears: number | null;
  city: string | null;
}

/** The identity-door candidate card: the disambiguation grade plus the
 * last visit - never a phone. */
export interface IntakeStartCandidateView extends IntakeIdentityCandidateView {
  lastVisitAt: string | null;
}

/** An active booking already riding the same phone (detection only). */
export interface IntakeStartBookedAppointmentView {
  appointmentId: number;
  appointmentTime: string;
  status: string;
  patientName: string | null;
  patientId: string | null;
  patientUmr: string | null;
}

export interface StartIntakeResult {
  /** Null on the zero-write selection step and on the linked outcome. */
  session: IntakeSessionView | null;
  alreadyStarted: boolean;
  /** 'started' | 'selection_required' (the desk must pick the person) |
   * 'linked' (the explicit pick linked this visit; no intake needed). */
  outcome: 'started' | 'selection_required' | 'linked';
  candidates: readonly IntakeStartCandidateView[];
  bookedAppointments: readonly IntakeStartBookedAppointmentView[];
  patientId: string | null;
  patientUmr: string | null;
  reconciliationPending: boolean;
  warnings: readonly string[];
}

/** The desk's explicit person pick at the intake identity door. */
export type IntakeStartChoice =
  | { action: 'link'; patientId: string }
  | { action: 'create_new' };

/** Backend POST /api/appointments/:id/intake/start. Body: { mode,
 * patientChoice? } - the optional patientChoice is the desk's explicit
 * person selection at the identity door (never sent unprompted). */
export async function startIntakeSession(
  appointmentId: number,
  mode: 'digital' | 'paper',
  patientChoice?: IntakeStartChoice,
  expectedBearer?: string,
): Promise<StartIntakeResult> {
  return portalApiPost<StartIntakeResult>(
    `/appointments/${appointmentId}/intake/start`,
    { mode, ...(patientChoice ? { patientChoice } : {}) },
    expectedBearer,
  );
}
